import Vue from "vue";
import VueRouter from "vue-router";
import VueMeta from "vue-meta";
import routes from "./routes";
import store from "../state/store";

Vue.use(VueRouter);
Vue.use(VueMeta, {
  // The component option name that vue-meta looks for meta info on.
  keyName: "page",
});

// ปลายทางหลัง login ของแต่ละ role — เรียงตาม precedence (ผู้ใช้ที่มีหลาย role
// ใช้ตัวแรกที่ match) ปลายทางทุกตัวถูกเช็คแล้วว่า role นั้นอยู่ใน allowRoles ของ
// route จริง (ดู routes.js) และตรงกับเมนูตัวแรกที่ role นั้นเห็นใน side-menu.js
const POST_LOGIN_ROUTE_BY_ROLE = [
  { role: "super_admin", routeName: "Pre Approve" }, // /admin/preapprove
  { role: "admin_approver_level1", routeName: "Pre Approve" }, // /admin/preapprove
  { role: "admin_approver_level2", routeName: "Approve" }, // /admin/approve
  { role: "oss_officer", routeName: "Report" }, // /dashboards/report
  { role: "data_analyst", routeName: "dashboard viz" }, // /viz
];

/**
 * หา route ปลายทางหลัง login จาก role ของผู้ใช้
 * @param {string[]} userRole
 * @returns {string|null} ชื่อ route — null = ไม่ต้อง redirect (ค้างที่ /main)
 */
function resolvePostLoginRoute(userRole) {
  // ผู้ประกอบการทั่วไปไม่มี role เลย (USER_ROLE = []) → นับเป็น vendor_business_owner
  if (userRole.length === 0) return "AddSME"; // /account/sme/add-sme
  const matched = POST_LOGIN_ROUTE_BY_ROLE.find(({ role }) => userRole.includes(role));
  // role ที่ไม่รู้จัก: ไม่ redirect — ส่งไป AddSME ไม่ได้เพราะ guard บังคับ
  // userRole.length === 0 จะโดนเด้งกลับ /main อยู่ดี
  return matched ? matched.routeName : null;
}

const router = new VueRouter({
  routes,
  mode: "history",
  scrollBehavior(to, from, savedPosition) {
    if (to.hash) {
      return {
        selector: to.hash,
        offset: {
          x: 0,
          y: 80,
        },
        behavior: "smooth",
      };
    } else if (to.path == from.path) {
      return {
        x: 0,
        y: 0,
      };
    } else if (savedPosition) {
      return savedPosition;
    } else {
      return {
        x: 0,
        y: 0,
      };
    }
  },
});

// function sleep(ms) {
//   return new Promise((resolve) => setTimeout(resolve, ms));
// }

router.beforeEach(async (to, from, next) => {
  // SSO destination flow needs to bypass every other guard — a logged-in
  // user from another tab must still be allowed to land on /sso/landing
  // (verify-inbound will force-terminate their old session before minting
  // a new one). Same for the /sso/error page so failures route cleanly.
  if (to.matched.some(r => r.meta.bypassAuth)) {
    next();
    return;
  }

  const redirectAfterLogout = localStorage.getItem("redirectAfterLogout");
  if (redirectAfterLogout) {
    localStorage.removeItem("redirectAfterLogout");
    next(redirectAfterLogout);
    return;
  }
  const isAuth = store.getters["authen/getUser"] || store.getters["govAuthen/isAuthenticate"];

  if (to.matched.some(r => r.meta.guestOnly) && isAuth) {
    next("/main");
    return;
  }
  const govAuth = await store.dispatch("govAuthen/setUserFromToken");
  if (
    to.matched.some(r => r.meta.requiresGovAuth) &&
    !to.matched.some(r => r.meta.requiresAuth)
  ) {
    if (!(govAuth || !!store.getters["authen/getUser"])) {
      return next({
        name: "governmentLogin"
      });
    }
  }
  if (
    govAuth &&
    to.matched.some(r => r.meta.requiresAuth) &&
    !to.matched.some(r => r.meta.requiresGovAuth)
  ) {
    return next("/main");
  }
  // if (to.name === "governmentLogin" && store.getters["govAuthen/isAuthenticate"]) {
  //   next("/main");
  //   return;
  // }
  if (store && !store.getters["authen/getUser"]) {
    await store.dispatch("authen/fetchAccessToken", { isRefresh: false });
  }

  const userRole = store.getters["authen/getRoles"];
  const redirectUrl = sessionStorage.getItem("redirectUrl");
  const userData = store.getters["authen/getUser"];
  // ห้ามใช้ localStorage "isAuthenticate" เป็นตัวกันซ้ำของ post-login redirect —
  // fetchAccessToken() ด้านบน set ค่านั้นไปแล้วทุกครั้งที่ token ยังใช้ได้ ทำให้
  // เงื่อนไข !isAuthenticate เป็น false เสมอ (บล็อก redirect เลยตายมาตั้งแต่
  // commit 08456d5d) จึงใช้ flag แยกที่มีแต่บล็อกนี้เท่านั้นที่เขียน
  const hasHandledFirstLogin = !!localStorage.getItem("postLoginRedirected");
  // if (to.name === "LoginV2" && store.getters["authen/getUser"]) {
  //   next("/main");
  //   return;
  // }
  if (to.matched.some((record) => record.meta.requiresAuth)) {
    if (govAuth) {
      next();
      return;
    }
    
    if (userData) {
      if (
        userRole.some((role) => to.meta.allowRoles.some((allowRoles) => allowRoles === role)) ||
        (to.meta.allowRoles.some((allowRoles) => allowRoles === "vendor_business_owner") && userRole.length === 0)
      ) {
        if (redirectUrl && redirectUrl !== "/loginv2") {
          sessionStorage.removeItem("redirectUrl");
          next(redirectUrl);
          return;
        }
        if (redirectUrl) {
          sessionStorage.removeItem("redirectUrl");
        }
        next();
        return;
      } else {
        next("/main");
        return;
      }
    } else {
      next({ name: "LoginV2", query: { redirectUrl: to.fullPath } });
      return;
    }
  } else {
    if (redirectUrl) {
      sessionStorage.removeItem("redirectUrl");
      sessionStorage.removeItem("firstLoginSession");
      window.location.href = redirectUrl
      return;
    }
    // Post-login redirect — ทำครั้งเดียวต่อการ login หนึ่งครั้ง
    // (firstLoginSession ถูก set ใน authen/fetchAccessToken, ส่วน
    //  postLoginRedirected ถูกล้างตอน setIsAuthenticate(false) = logout / token หมดอายุ)
    if (sessionStorage.getItem("firstLoginSession") == "1" && userData && !hasHandledFirstLogin) {
      sessionStorage.removeItem("firstLoginSession");
      localStorage.setItem("postLoginRedirected", "1");

      // เฉพาะ login ผ่าน Digital ID / DBD ID เท่านั้น — ฝั่ง SSO (login_by = "sso")
      // มี redirectPath ของตัวเองที่ sso/landing.vue เป็นคนพาไป ห้าม override
      const isIdpLogin = userData.login_by === "digital_id" || userData.login_by === "dbd";
      const postLoginRouteName = resolvePostLoginRoute(userRole);
      // backToPath ของ loginDigitalId/loginDbdId คือ /main เสมอ จึง redirect
      // เฉพาะตอนที่ปลายทางเป็นหน้าหลัก — ถ้าผู้ใช้กำลังเปิดหน้าอื่นอยู่
      // (เช่น เปิด tab ใหม่ทั้งที่ login ค้างไว้) ให้อยู่หน้านั้นต่อ
      if (isIdpLogin && postLoginRouteName && to.name === "default") {
        next({ name: postLoginRouteName });
        return;
      }
    }

    next();
    return;
  }
});

export default router;
